import {
  composeRenderProps,
  FieldErrorProps,
  Group,
  GroupProps,
  InputProps,
  LabelProps,
  FieldError as RACFieldError,
  Input as RACInput,
  Label as RACLabel,
  Text,
  TextProps,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { tv, VariantProps } from 'tailwind-variants';
import { composeTailwindRenderProps, focusRing } from './utils';

export function ClarionLabel(props: LabelProps) {
  return (
    <RACLabel
      {...props}
      className={twMerge(
        'text-sm text-neutral-600 dark:text-neutral-300 font-medium cursor-default w-fit',
        props.className,
      )}
    />
  );
}

export function ClarionDescription(props: TextProps) {
  return (
    <Text
      {...props}
      slot="description"
      className={twMerge('text-xs opacity-70', props.className)}
    />
  );
}

export function ClarionFieldError(props: FieldErrorProps) {
  return (
    <RACFieldError
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        'text-sm text-red-600 dark:text-red-300 forced-colors:text-[Mark]',
      )}
    />
  );
}

export const fieldBorderStyles = tv({
  base: 'transition',
  variants: {
    isFocusWithin: {
      false:
        'border-neutral-400 hover:border-neutral-500 dark:border-neutral-500 dark:hover:border-neutral-400 forced-colors:border-[ButtonBorder]',
      true: 'border-purple-600 dark:border-purple-300 forced-colors:border-[Highlight]',
    },
    isInvalid: {
      true: 'border-red-500 dark:border-red-300 forced-colors:border-[Mark]',
    },
    isDisabled: {
      true: 'border-neutral-200 dark:border-neutral-700 forced-colors:border-[GrayText]',
    },
  },
});

export const fieldGroupStyles = tv({
  extend: focusRing,
  base: 'group flex items-center box-border bg-white dark:bg-neutral-900/50 forced-colors:bg-[Field] border rounded-lg overflow-hidden transition',
  variants: {
    ...fieldBorderStyles.variants,
    size: {
      small: 'h-6',
      medium: 'h-8',
      large: 'h-10',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

export interface ClarionFieldGroupProps extends GroupProps, VariantProps<typeof fieldGroupStyles> {}

export function ClarionFieldGroup({ size, ...props }: ClarionFieldGroupProps) {
  return (
    <Group
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        fieldGroupStyles({ ...renderProps, size, className }),
      )}
    />
  );
}

export function ClarionInput(props: InputProps) {
  return (
    <RACInput
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        'px-3 py-0 h-full flex-1 min-w-0 border-0 outline-none bg-transparent text-sm text-neutral-800 dark:text-neutral-200 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 disabled:text-neutral-200 dark:disabled:text-neutral-600 disabled:placeholder:text-neutral-200 dark:disabled:placeholder:text-neutral-600 disabled:cursor-not-allowed [-webkit-tap-highlight-color:transparent]',
      )}
    />
  );
}
